import React, { useState } from 'react';
import {
  AutocompleteInput,
  Datagrid,
  DateField,
  DateInput,
  DeleteButton,
  Edit,
  FormTab,
  ReferenceInput,
  ReferenceManyField,
  SelectInput,
  TabbedForm,
  TextField,
  TextInput,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRedirect,
  useResourceContext,
  Toolbar,
  ToolbarClasses
} from 'react-admin';
import { useFormContext, useFormState } from 'react-hook-form';
import {
  Box,
  Button,
  Breadcrumbs,
  Container,
  Divider,
  Grid,
  Link as MuiLink,
  Portal,
  Typography,
  useMediaQuery,
  type Theme
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AddOpportunityServiceButton } from './components/AddOpportunityServiceButton.tsx';
import { SyncCloseDateOnStatus } from './components/SyncCloseDateOnStatus.tsx';
import { AddActivityButton } from '../components/AddActivityButton.tsx';
import { EditActivityButton } from '../components/EditActivityButton.tsx';
import { ConditionalFollowUpDateField } from '../components/ConditionalFollowUpDateField.tsx';
import AppBarActionsPortal from '../components/AppBarActionsPortal.tsx';
import { useKeyboardInset } from '../components/useKeyboardInset.ts';
import { saveBarZIndex } from '../theme.ts';
import { ContactLinkButton } from '../components/ra-forms/ContactLinkButton.tsx';
import { NoteListSection } from '../components/notes/NoteListSection.tsx';
import { DocumentListSection } from '../components/documents/DocumentListSection.tsx';
import { OPPORTUNITY_STATUS_CHOICES } from '../types/OpportunityStatusChoices.ts';
import { validateFollowUpIfLostButTrack } from '../validators/opportunityValidators.ts';
import OpportunityProfileInline from './OpportunityProfileInline.tsx';
import { ProfilePdfActions } from './components/ProfilePdfActions.tsx';
import { opportunityProfileKeys } from './opportunityProfileKeys.ts';

type OpportunityEditToolbarProps = {
  onSave: (values: Record<string, unknown>) => Promise<Record<string, unknown> | undefined>;
};

const OpportunityEditToolbar: React.FC<OpportunityEditToolbarProps> = ({ onSave, ...props }) => {
  const { control, handleSubmit, reset } = useFormContext();
  const { dirtyFields, isSubmitting, isValidating } = useFormState({ control });
  const redirect = useRedirect();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTab = searchParams.get('returnTab');
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('md'), { noSsr: true });
  const keyboardInset = useKeyboardInset();
  const resource = useResourceContext();
  const record = useRecordContext<{ id?: number }>();
  const hasChanges = Object.keys(dirtyFields).length > 0;
  const disabled = !hasChanges || isSubmitting || isValidating;

  const closeEditor = () => {
    if (!resource) return;
    if (record?.id) {
      const tabQuery =
        returnTab && returnTab !== 'overview' ? `?tab=${encodeURIComponent(returnTab)}` : '';
      navigate(`/${resource}/${record.id}/show${tabQuery}`);
    } else {
      redirect('list', resource);
    }
  };

  const runSave = async (redirectTo?: 'show' | 'edit' | false) => {
    let savedRecord: Record<string, unknown> | undefined;
    try {
      await handleSubmit(async (values) => {
        savedRecord = await onSave(values);
      })();
    } catch {
      // The save handler already notified the user. Catch only after
      // react-hook-form records the failed submission.
      return;
    }
    if (savedRecord) {
      reset(savedRecord);
      if (redirectTo && resource) {
        if (redirectTo === 'show') {
          closeEditor();
        } else if (redirectTo === 'edit' && record?.id) {
          redirect('edit', resource, record.id);
        }
      }
    }
  };

  return (
    <>
      <AppBarActionsPortal>
        <Button variant="text" color="inherit" size="medium" onClick={closeEditor}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="medium"
          onClick={() => runSave('show')}
          disabled={disabled}
        >
          Save & Close
        </Button>
        <Button variant="outlined" size="medium" onClick={() => runSave(false)} disabled={disabled}>
          Save
        </Button>
      </AppBarActionsPortal>

      <Box sx={{ display: { xs: 'block', md: 'none' }, height: 72 }} aria-hidden />
      {/* On desktop this stays in the form as its footer. */}
      <Portal disablePortal={isDesktop}>
        <Toolbar
          {...props}
          sx={{
            display: 'flex',
            position: { xs: 'fixed', md: 'static' },
            left: { xs: 0, md: 'auto' },
            right: { xs: 0, md: 'auto' },
            bottom: { xs: keyboardInset, md: 'auto' },
            zIndex: saveBarZIndex,
            width: '100%',
            boxSizing: 'border-box',
            px: { xs: 1, sm: 1.5, md: 2 },
            pt: { xs: 1, md: 1.5 },
            pb: { xs: 'calc(8px + env(safe-area-inset-bottom))', md: 1.5 },
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: { xs: '0 -6px 20px rgba(16, 24, 40, 0.08)', md: 'none' },
            // react-admin's phone toolbar style outranks the phone values above with its own
            // bottom 0, z-index 2 and 16px padding
            [`&.${ToolbarClasses.mobileToolbar}`]: {
              bottom: keyboardInset,
              zIndex: saveBarZIndex,
              px: 1,
              pt: 1,
              pb: 'calc(8px + env(safe-area-inset-bottom))'
            }
          }}
        >
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              width: '100%'
            }}
          >
            <DeleteButton mutationMode="pessimistic" redirect="list" sx={{ mr: 'auto' }} />
            <Button variant="text" color="inherit" size="small" onClick={closeEditor}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => runSave('show')}
              disabled={disabled}
            >
              Save & Close
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => runSave(false)}
              disabled={disabled}
            >
              Save
            </Button>
          </Box>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              width: '100%'
            }}
          >
            <DeleteButton mutationMode="pessimistic" redirect="list" />
          </Box>
        </Toolbar>
      </Portal>
    </>
  );
};

const OpportunityEditFormInner: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const record = useRecordContext<{ id?: number }>();
  const [profileSnapshot, setProfileSnapshot] = useState<Record<string, unknown> | null>(null);

  const handleSave = async (values: Record<string, unknown>) => {
    if (!record?.id) return;

    const opportunityData: Record<string, unknown> = {};
    const profileData: Record<string, unknown> = profileSnapshot ? { ...profileSnapshot } : {};
    Object.entries(values).forEach(([key, value]) => {
      if (key === 'profileLoaded') return;
      if (opportunityProfileKeys.has(key)) {
        if (value !== undefined) {
          profileData[key] = value;
        }
      } else {
        opportunityData[key] = value;
      }
    });

    const opportunityId = record.id;

    try {
      const updatedOpportunity = await dataProvider.update('opportunities', {
        id: opportunityId,
        data: opportunityData,
        previousData: record
      });

      let updatedProfile: Record<string, unknown> | null = null;
      if (Object.keys(profileData).length) {
        try {
          const result = await dataProvider.update('opportunity-profiles', {
            id: opportunityId,
            data: { ...profileData, opportunityId },
            previousData: {}
          });
          updatedProfile = result.data ?? null;
        } catch {
          const result = await dataProvider.create('opportunity-profiles', {
            data: { ...profileData, opportunityId }
          });
          updatedProfile = result.data ?? null;
        }
      }

      notify('Saved', { type: 'success' });
      if (updatedProfile) {
        setProfileSnapshot(updatedProfile);
      }
      const mergedValues = {
        ...values,
        ...(updatedProfile ?? profileData),
        id: updatedOpportunity.data?.id ?? record.id
      };
      return mergedValues;
    } catch (error) {
      notify(`Error: ${error?.toString?.() || 'Unknown error'}`, { type: 'error' });
      throw error;
    }
  };

  const handleFormSubmit = async (values: Record<string, unknown>) => {
    try {
      await handleSave(values);
      // React Admin treats a returned object as a submission-error map.
      // Toolbar saves need the saved record, but native form submission does not.
      return undefined;
    } catch (error) {
      return {
        root: {
          serverError: error instanceof Error ? error.message : 'Save failed'
        }
      };
    }
  };

  return (
    <TabbedForm
      validate={validateFollowUpIfLostButTrack}
      warnWhenUnsavedChanges
      onSubmit={handleFormSubmit}
      toolbar={<OpportunityEditToolbar onSave={handleSave} />}
    >
      <FormTab label="Details">
        <Container maxWidth="md">
          <SyncCloseDateOnStatus />
          <Typography variant="h5" gutterBottom>
            Opportunity Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex' }}>
                <ReferenceInput source="contactId" reference="contacts" label="Contact" fullWidth>
                  <AutocompleteInput
                    optionText={(record) => `${record.firstName} ${record.lastName}`}
                    filterToQuery={(searchText: string) => ({ q: searchText })}
                    helperText={false}
                  />
                </ReferenceInput>
                <ContactLinkButton />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SelectInput
                helperText={false}
                source="status"
                label="Status"
                choices={OPPORTUNITY_STATUS_CHOICES}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReferenceInput
                source="opportunityStageId"
                reference="opportunity-stages"
                label="Stage"
                fullWidth
              >
                <SelectInput optionText="name" helperText={false} />
              </ReferenceInput>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateInput
                source="followUpDate"
                label="Follow Up Date"
                helperText={false}
                format={(v) => (v && v.includes('T') ? v.split('T')[0] : v)}
                parse={(v) => (v ? v : null)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateInput
                source="dateOpened"
                format={(v) => (v && v.includes('T') ? v.split('T')[0] : v)}
                parse={(v) => (v ? v : null)}
                helperText={false}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateInput
                source="dateClosed"
                format={(v) => (v && v.includes('T') ? v.split('T')[0] : v)}
                parse={(v) => (v ? v : null)}
                helperText={false}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6">Referred By</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ReferenceInput
                source="referredByContactId"
                reference="contacts"
                label="Referred By Contact"
                fullWidth
                helperText={false}
              >
                <AutocompleteInput
                  optionText={(record) => `${record.firstName} ${record.lastName}`}
                  filterToQuery={(searchText: string) => ({ q: searchText })}
                  helperText={false}
                />
              </ReferenceInput>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <ReferenceInput
                source="referredByGroupId"
                reference="groups"
                label="Referred By Group"
                fullWidth
                helperText={false}
              >
                <AutocompleteInput
                  optionText="name"
                  filterToQuery={(searchText: string) => ({ q: searchText })}
                  helperText={false}
                />
              </ReferenceInput>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextInput
                source="notes"
                label="General Notes"
                multiline
                rows={4}
                fullWidth
                helperText={false}
              />
            </Grid>
          </Grid>
          <Divider sx={{ marginY: 2 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant={'subtitle1'} gutterBottom>
                Programs
              </Typography>
              <ReferenceManyField
                label="Services"
                reference="opportunity-services"
                target="opportunityId"
              >
                <Datagrid
                  bulkActionButtons={false}
                  empty={
                    <Box>
                      <small>No programs yet.</small>
                    </Box>
                  }
                >
                  <TextField source="displayName" label={'Name'} />
                  <DeleteButton
                    mutationMode="pessimistic"
                    redirect={false}
                    label="Remove"
                    confirmTitle="Are you sure?"
                    confirmContent="This will remove the program from the opportunity."
                  />
                </Datagrid>
              </ReferenceManyField>
              <Box mt={2}>
                <AddOpportunityServiceButton />
              </Box>
            </Grid>
          </Grid>
          <Divider sx={{ marginY: 2 }} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant={'subtitle1'} gutterBottom>
                Activity
              </Typography>
              <ReferenceManyField
                label="Activities"
                reference="opportunity-activities"
                target="opportunityId"
              >
                <Datagrid
                  bulkActionButtons={false}
                  empty={
                    <Box>
                      <small>No activity yet.</small>
                    </Box>
                  }
                >
                  <TextField source="type" />
                  <TextField source="note" />
                  <DateField
                    source="dateSent"
                    label="Activity Date"
                    locales="en-US"
                    options={{ timeZone: 'UTC' }}
                  />

                  <ConditionalFollowUpDateField
                    source="followUpDate"
                    label="Follow Up On"
                    locales="en-US"
                    options={{ timeZone: 'UTC' }}
                  />
                  <EditActivityButton
                    keyName={'opportunityId'}
                    resource={'opportunity-activities'}
                  />
                  <DeleteButton
                    mutationMode="pessimistic"
                    redirect={false}
                    label="Remove"
                    confirmTitle="Are you sure?"
                    confirmContent="This will remove the activiy from the opportunity."
                  />
                </Datagrid>
              </ReferenceManyField>
              <Box my={2}>
                <AddActivityButton keyName={'opportunityId'} resource={'opportunity-activities'} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </FormTab>
      <FormTab label="Opportunity Profile" path="profile">
        <Container maxWidth="md">
          <Box sx={{ mb: 2 }}>
            <ProfilePdfActions />
          </Box>
          <OpportunityProfileInline
            onLoaded={setProfileSnapshot}
            externalProfile={profileSnapshot}
          />
        </Container>
      </FormTab>
      <FormTab label="Notes" path="notes">
        <Container maxWidth="md">
          <NoteListSection entityType="OPPORTUNITY" />
        </Container>
      </FormTab>
      <FormTab label="Documents" path="documents">
        <Container maxWidth="md">
          <DocumentListSection entityType="OPPORTUNITY" />
        </Container>
      </FormTab>
    </TabbedForm>
  );
};

const OpportunityEdit: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/opportunities">
          Opportunities
        </MuiLink>
        <Typography color="text.primary">Edit</Typography>
      </Breadcrumbs>
      <Edit mutationMode={'pessimistic'}>
        <OpportunityEditFormInner />
      </Edit>
    </Container>
  );
};

export default OpportunityEdit;
